<script setup>
const { t, locale, locales } = useI18n()
const route = useRoute()
const switchLocalePath = useSwitchLocalePath()

const SITE_URL = 'https://www.dexon-technology.com'

useHead(() => ({
  htmlAttrs: { lang: locale.value },
  title: t('meta.title'),
  meta: [{
    name: 'description',
    content: t('meta.description'),
  }],
  link: [
    // entry.css inlines @font-face rules pointing straight at fonts.gstatic.com (no
    // fonts.googleapis.com hop) — preconnect so that render-blocking fetch starts sooner.
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    // Storyblok's image CDN — almost every page's LCP image comes from here.
    { rel: 'preconnect', href: 'https://a.storyblok.com' },
    { rel: 'canonical', href: `${SITE_URL}${route.path.replace(/(.)\/+$/, '$1')}` },
    ...locales.value.map(loc => ({
      rel: 'alternate',
      hreflang: loc.code,
      href: `${SITE_URL}${switchLocalePath(loc.code)}`,
    })),
    { rel: 'alternate', hreflang: 'x-default', href: `${SITE_URL}${switchLocalePath('en')}` },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': organizationGraph(),
      }),
    },
  ],
}))
</script>

<template>
  <div>
    <VitePwaManifest />
    <NuxtLayout>
      <NuxtPage />
      <CookieConsent />
      <NuxtLoadingBar :duration="1000" />
    </NuxtLayout>
    <ClientOnly>
      <noscript>
        <img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=6961476&fmt=gif" />
      </noscript>
    </ClientOnly>
  </div>
</template>

<style>
  .delay-500 {
    transition-delay: 500ms;
  }

  .delay-500 {
    transition-delay: 500ms;
  }

  .delay-600 {
    transition-delay: 600ms;
  }

  .delay-700 {
    transition-delay: 700ms;
  }

  .delay-800 {
    transition-delay: 800ms;
  }

  .delay-900 {
    transition-delay: 900ms;
  }

  .delay-1000 {
    transition-delay: 1000ms;
  }

  .delay-1100 {
    transition-delay: 1100ms;
  }

  .delay-1200 {
    transition-delay: 1200ms;
  }

  .delay-1300 {
    transition-delay: 1300ms;
  }

  .delay-1400 {
    transition-delay: 1400ms;
  }

  .delay-1500 {
    transition-delay: 1500ms;
  }

  ul.formkit-messages {
    @apply  !pl-0 pt-2;
  }

  iframe {
    @apply lg: max-h-full md:max-h-[400px] sm:max-h-[350px] max-h-[300px] !important;
  }

  /* Hero Container */
  .hero-container {
    @apply relative z-10 lg: min-h-[calc(100vh-300px)] sm:min-h-[calc(100vh-400px)] min-h-[calc(100vh-250px)];
  }

  .hexagon {
    @apply lg: min-h-[calc(100vh-300px)] sm:min-h-[calc(100vh-400px)] min-h-[calc(100vh-250px)];
  }

  .hero-container .hero-content p {
    @apply line-clamp-4 text-lg font-normal first: pb-2 leading-7 !text-white/90;
  }

  .hero-container .hero-content p span {
    @apply  !text-inherit;
  }

  .columns-content h1,
  .columns-content h2,
  .columns-content h3,
  .columns-content h4,
  .columns-content h5,
  .columns-content h6,
  .columns-content b,
  .columns-content strong {
    @apply  !font-bold !text-primary;
  }

.columns-content h1 > span[class*='m'],
.columns-content h2 > span[class*='m'],
.columns-content h3 > span[class*='m'],
.columns-content h4 > span[class*='m'],
.columns-content h5 > span[class*='m'],
.columns-content h6 > span[class*='m'],
.columns-content p > span[class*='m'] {
  display: inherit;
}

  .full-grid .columns-content img {
    @apply lg: rounded-lg md:rounded-md rounded-sm my-4 max-w-4xl w-full;
  }

  .columns-content p {
    @apply [&:not(:last-child)]:mb-3 text-lg leading-[1.9];
  }

  .columns-content.columns-lg p {
    @apply [&:not(:last-child)]:!mb-3 text-lg;
  }

  .columns-content ul {
    @apply mb-3;
  }

  .columns-content.columns-lg ul {
    @apply mb-4;
  }

  .columns-content ul li {
    @apply list-disc;
  }

  .columns-content:not(.columns-content-dark) * {
    @apply text-primary;
  }

  .columns-content.columns-content-dark * {
    @apply  !text-white/90;
  }

  .columns-content i,
  .columns-content em,
  .columns-content .italic {
    font-style: italic;
  }

  .full-grid .columns-content p:has(.richtext-align-center),
  .full-grid .columns-content h1:has(.richtext-align-center),
  .full-grid .columns-content h2:has(.richtext-align-center),
  .full-grid .columns-content h3:has(.richtext-align-center),
  .full-grid .columns-content h4:has(.richtext-align-center),
  .full-grid .columns-content h5:has(.richtext-align-center),
  .full-grid .columns-content h6:has(.richtext-align-center) {
    @apply text-center;
  }

  .full-grid .columns-content p:has(.richtext-align-right),
  .full-grid .columns-content h1:has(.richtext-align-right),
  .full-grid .columns-content h2:has(.richtext-align-right),
  .full-grid .columns-content h3:has(.richtext-align-right),
  .full-grid .columns-content h4:has(.richtext-align-right),
  .full-grid .columns-content h5:has(.richtext-align-right),
  .full-grid .columns-content h6:has(.richtext-align-right) {
    @apply text-right;
  }

  .full-grid .columns-content p:has(.richtext-align-left),
  .full-grid .columns-content h1:has(.richtext-align-left),
  .full-grid .columns-content h2:has(.richtext-align-left),
  .full-grid .columns-content h3:has(.richtext-align-left),
  .full-grid .columns-content h4:has(.richtext-align-left),
  .full-grid .columns-content h5:has(.richtext-align-left),
  .full-grid .columns-content h6:has(.richtext-align-left) {
    @apply text-left;
  }

  .full-grid .columns-content p:has(.richtext-align-justify),
  .full-grid .columns-content h1:has(.richtext-align-justify),
  .full-grid .columns-content h2:has(.richtext-align-justify),
  .full-grid .columns-content h3:has(.richtext-align-justify),
  .full-grid .columns-content h4:has(.richtext-align-justify),
  .full-grid .columns-content h5:has(.richtext-align-justify),
  .full-grid .columns-content h6:has(.richtext-align-justify) {
    @apply text-justify;
  }

  .columns-content h2 {
    @apply mb-custom-md text-xl sm:text-2xl font-bold md:text-3xl leading-[1.4];
  }

  .columns-content h3 {
    @apply mb-custom-sm text-lg sm:text-xl font-bold md:text-2xl font-bold;
  }

  .columns-content h4,
  .columns-content h5,
  .columns-content h4 *,
  .columns-content h5 * {
    @apply mb-custom-sm text-lg font-bold;
  }

  /* form */
  .form-content.dark h2 {
    @apply text-white;
  }

  .form-content h2 {
    @apply mx-auto text-3xl font-bold text-primary md: text-4xl xl:text-5xl mb-custom-md;
  }

  .form-content>h2 {
    @apply mb-0;
  }

  .form-content h2~p:nth-child(3) {
    @apply mt-custom-md;
  }

  .form-content.dark p {
    @apply text-white/80;
  }

  .form-content p {
    @apply mb-custom-md text-xl leading-[1.8] text-primary/80;
  }

  /* Article */

  ol {
    @apply list-decimal mb-custom-sm pl-4;
  }

  ol li {
    @apply mb-2;
  }

  .article.small-image img {
    @apply  !max-w-xl w-full;
  }

  .article.medium-image img {
    @apply  !max-w-4xl w-full;
  }

  .article h2 {
    @apply mb-custom-md text-xl sm: text-2xl leading-[1.4] capitalize !important;
  }

  .article h3 {
    @apply mb-custom-xs text-xl font-bold leading-[1.6];
  }

  .article h4,
  .article h5 {
    @apply mb-custom-xs text-lg font-bold leading-[1.6];
  }

  .article b,
  .article strong {
    @apply text-primary;
  }

  .article p {
    @apply leading-[2] mb-custom-md;
  }

  .article.career p {
    @apply  !mb-2;
  }

  .article p:last-child {
    @apply mb-0;
  }

  .article:not(.popup-content) p:not(table p, table td, table th),
  .article:not(.popup-content) span,
  .article:not(.popup-content) ul li {
    @apply  !text-primary/80;
  }

  .article.popup-content h1,
  .article.popup-content h2,
  .article.popup-content h3,
  .article.popup-content h4,
  .article.popup-content h5 {
    @apply  !text-white;
  }

  .article.popup-content p,
  .article.popup-content span,
  .article.popup-content b,
  .article.popup-content ul {
    @apply  !text-white !leading-[1.9];
  }

  .article .day-badge span {
    @apply  !text-white;
  }

  .article a {
    @apply underline hover: text-secondary-red-100 text-primary;
  }

  .article a b {
    @apply hover: text-secondary-red-100;
  }

  .article ul {
    @apply list-disc mb-custom-sm;
  }

  .article ul li p {
    @apply mb-0;
  }

  .article ul li {
    @apply mb-custom-sm;
  }

  .article.auto-width img {
    @apply w-auto !important;
  }

  .article img {
    @apply w-auto max-w-full rounded-md;
  }

  .article h1,
  .article h2,
  .article h3,
  .article h4,
  .article h5,
  .article h6 {
    @apply font-bold;
  }

  .article blockquote {
    @apply bg-secondary-gray-200 p-4 rounded-md text-center;
  }

  .article blockquote p {
    @apply last: mb-0 mb-1;
  }

  .article blockquote p {
    @apply text-blue-500 font-semibold;
  }

  .article blockquote {
    @apply my-custom-md;
  }

  /* Carousel */
  div .carousel__viewport,
  .carousel {
    @apply focus-visible: outline-none focus:outline-none;
  }

  div>.carousel-custom-pagination ol.carousel__pagination {
    @apply min-h-[40px] items-center !relative m-0 gap-x-3;
  }

  div>.carousel-custom-pagination ol.carousel__pagination button.carousel__pagination-button {
    @apply w-[16.5px] h-[16.5px] m-0 rounded-full bg-white after: content-none;
  }

  div>.carousel-custom-pagination ol.carousel__pagination button.carousel__pagination-button--active {
    @apply bg-secondary-red-100;
  }

  .carousel-custom-navigation .carousel__pagination-item {
    @apply !mb-0;
  }

  .carousel-custom-navigation .carousel__pagination-button::after {
    content: unset !important;
  }

  div>.carousel-custom-navigation .carousel__prev,
  div>.carousel-custom-navigation .carousel__next {
    @apply w-[40px] h-[40px] !relative !inset-y-0 translate-y-0 m-0;
  }

  div>.carousel-custom-navigation .carousel__prev {
    @apply  !left-auto;
  }

  div>.carousel-custom-navigation .carousel__next {
    @apply  !right-auto;
  }

  .mb-custom-lg {
    @apply  !mb-[30px];
  }

  .header-pattern-light {
    background-image: url(/svg/header-pattern-light.svg);
  }

  html {
    position: unset !important;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
    -ms-touch-action: manipulation;
    touch-action: manipulation;
    font-weight: 400;
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
    height: 100%;
    @apply font-sans !important;
  }

  a:active,
  a:focus,
  input:active,
  input:focus,
  textarea:focus-visible,
  button:focus-visible,
  a:focus-visible {
    outline: none !important;
    stroke: none !important;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s !important;
    -webkit-text-fill-color: #140A4B !important;
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

  .container {
    @apply box-border 3xl:max-w-[1280px] 2xl:max-w-[1200px] xl:max-w-[1000px] lg:max-w-[992px] md:max-w-[768px] sm:max-w-[600px] max-w-full
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  .model-label {
    @apply opacity-100 leading-4 text-[13px] bg-secondary-red-100 rounded-[3px] px-2 py-1 shadow-custom-lg shadow-shadow-light/20 text-white font-semibold;
  }

  #questions-form input:-webkit-autofill,
  #questions-form input:-webkit-autofill:hover,
  #questions-form input:-webkit-autofill:focus,
  #questions-form input:-webkit-autofill:active {
    -webkit-text-fill-color: #fff !important;
  }

  h2 {
    @apply leading-[1.2] !important;
  }

  ul {
    @apply pl-4;
  }

  li {
    @apply mb-1;
  }

  p,
  ul {
    @apply text-primary/80 leading-[1.9];
  }

  .container {
    @apply px-4;
  }

  .th h1,
  .th h2,
  .th h3,
  .th h4,
  .th h5,
  .th button {
    @apply font-semibold;
  }

  .th .main-menu p {
    @apply font-medium;
  }

  .th .submenu-inner a,
  .th .submenu-inner a span,
  .th .submenu-inner a div {
    @apply text-sm font-[400];
  }

  html {
    @apply bg-white text-primary
  }

  .btn-sm {
    @apply text-base px-6 py-3.5 leading-[3] !important;
  }

  :not(html, body)::-webkit-scrollbar {
    width: 6px;
    height: 8px;
  }

  :not(html, body)::-webkit-scrollbar-track {
    @apply bg-slate-100;
  }

  :not(html, body)::-webkit-scrollbar-thumb {
    @apply bg-slate-300 rounded-sm;
    border-radius: 50px
  }

  html::-webkit-scrollbar {
    width: 8px;
  }

  html::-webkit-scrollbar-track {
    @apply bg-slate-100;
  }

  html::-webkit-scrollbar-thumb {
    @apply bg-slate-300 border-secondary-gray-100 rounded-sm;
    border-radius: 50px
  }

  .submenu {
    @apply lg: min-h-[370px] overflow-y-auto overflow-x-hidden h-[calc(100vh-42px)];
  }

  header a.router-link-active.router-link-exact-active:not(.bg-red-gradient),
  header a.router-link-active.router-link-exact-active:not(.bg-red-gradient) p {
    @apply  !text-secondary-red-100;
  }

  .main-menu a.router-link-active.router-link-exact-active .main-menu-inner {
    @apply lg: scale-[95%];
  }

  .menu-item *,
  .menu-item *:active,
  .menu-item *:focus,
  .menu-item *:visited {
    @apply  border-0 outline-none;
  }

  a.router-link-active.router-link-exact-active.submenu--list .arrow-right {
    @apply scale-100;
  }

  a.router-link-active.router-link-exact-active.submenu--list .arrow-up.not-active {
    @apply rotate-90;
  }

  .header-sm {
    @apply z-10 overflow-hidden bg-primary before: content-empty before:absolute lg:before:right-[-20rem] xl:before:right-[-18rem] 2xl:before:right-[-14rem] before:inset-y-0 before:w-full before:bg-no-repeat before:bg-right before:z-[-1];
  }

  .header-sm::before {
    background-image: url(/svg/header-pattern-light.svg);
  }

  .affix-pattern {
    @apply before: bg-none before:content-empty before:absolute before:-right-[50%] lg:before:-right-[35%] xl:before:-right-[25%] 2xl:before:-right-[18%] 3xl:before:-right-[10%] before:inset-y-0 before:w-full before:bg-no-repeat before:bg-right before:z-[-1];
  }

  .header-lg .container {
    @apply lg:h-[60px] h-full;
  }

  .header-lg .menu-item {
    @apply last:pr-0
  }

  .btn-primary {
    @apply text-sm px-3.5 py-1.5 rounded-full;
  }

  .bg-red-gradient {
    @apply bg-gradient-to-l from-secondary-red-100 to-secondary-red-200;
  }

  /* .breadcrumbs a.router-link-active.router-link-exact-active {
        @apply text-secondary-red-100 font-semibold;
    } */
</style>
